import { useEffect, useMemo, useState } from 'react';
import { flows, media } from '@repletoryreach/sdk-api';
import { Stack, Group, Button, Text, Card } from '@repletoryreach/sdk-ui';
import { Alert, Stepper } from '@repletoryreach/sdk-ui/data-display';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { Dropzone } from '@repletoryreach/sdk-ui/dropzone';
import { TextInput, Textarea, Select, Checkbox } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../i18n/I18nProvider';
import { HttpAuthFields } from '../flows/builder/components/HttpAuthFields';
import type { JsonObject } from '../flows/builder/mapper';
import {
  DOC_ACCEPT, actionFrom, connectionInput, endpointTitle, firstToTry, isPdf, pointRows, pointsInput, slug, startingConnection,
  type PointRow
} from './api-docs';

const MUTED = 'var(--rr-color-text-muted)';
const ERROR = 'var(--rr-color-error, #dc2626)';
type Step = 'docs' | 'connect' | 'choose' | 'review';
const STEPS: Step[] = ['docs', 'connect', 'choose', 'review'];
const MAX_DRAFTS = 12;

interface ConnDraft { name: string; base_url: string; auth: JsonObject; headers?: Record<string, string> }
interface SourceRow { add: boolean; source: flows.LiveSourceInput; examples: string; points: PointRow[]; draft: flows.ApiDraft }
interface ActionRow { add: boolean; index: number; name: string }

const message = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);

/**
 * Add from API documentation (owner, 19 October 2026): upload an API's documentation (OpenAPI or Swagger, a Postman
 * collection, Markdown or a PDF), sign in once, tick the endpoints wanted, and review what the AI drafted for each:
 * what it answers, the questions customers ask, the details taken from a question and the data points a flow can read.
 * Endpoints that answer become live sources; endpoints that do something are kept as actions for flows' Web API steps.
 * With `connection`, more endpoints are added to a saved connection.
 */
export function ApiWizard({ opened, connection, onClose, onDone }: {
  opened: boolean;
  connection?: flows.ApiConnection | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const t = useT();
  const [step, setStep] = useState<Step>('docs');
  const [pasted, setPasted] = useState('');
  const [reading, setReading] = useState(false);
  const [readError, setReadError] = useState<string | null>(null);
  const [read, setRead] = useState<flows.ApiDocsRead | null>(null);
  const [conn, setConn] = useState<ConnDraft>({ name: '', base_url: '', auth: {} });
  const [tryAt, setTryAt] = useState(-1);
  const [tryValues, setTryValues] = useState<Record<string, string>>({});
  const [trying, setTrying] = useState(false);
  const [tried, setTried] = useState<flows.ApiCallTest | null>(null);
  const [tryError, setTryError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [reads, setReads] = useState<Set<number>>(new Set());
  const [acts, setActs] = useState<Set<number>>(new Set());
  const [drafting, setDrafting] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [aiNote, setAiNote] = useState<string | null>(null);
  const [sources, setSources] = useState<SourceRow[]>([]);
  const [actions, setActions] = useState<ActionRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!opened) return;
    setStep('docs'); setPasted(''); setReading(false); setReadError(null); setRead(null);
    setConn(startingConnection(null, connection)); setTryAt(-1); setTryValues({}); setTried(null); setTryError(null);
    setFilter(''); setReads(new Set()); setActs(new Set()); setDraftError(null); setAiNote(null);
    setSources([]); setActions([]); setSaveError(null);
  }, [opened]); // eslint-disable-line react-hooks/exhaustive-deps

  const doc = read?.doc ?? null;
  const endpoints = doc?.endpoints ?? [];
  const placesLeft = read?.places_left ?? null;
  const maxReads = Math.min(MAX_DRAFTS, placesLeft ?? MAX_DRAFTS);
  const connInput = () => connectionInput(conn, doc);
  const connRef = () => (connection ? { connection_id: connection.id, connection: connInput() } : { connection: connInput() });

  // ── 1. The documentation ──────────────────────────────────────────────

  async function readDocs(source: { text: string } | { file: File }) {
    setReading(true); setReadError(null);
    try {
      let res: Awaited<ReturnType<typeof flows.readApiDocs>>;
      if ('file' in source && isPdf(source.file)) {
        const uploaded = await media.upload(source.file);
        res = await flows.readApiDocs({ media_id: uploaded.data.id, file_name: source.file.name });
      } else {
        const text = 'file' in source ? await source.file.text() : source.text;
        res = await flows.readApiDocs({ text, file_name: 'file' in source ? source.file.name : undefined });
      }
      setRead(res.data);
      if (!connection) setConn(startingConnection(res.data.doc, null));
      setTryAt(firstToTry(res.data.doc));
      setStep('connect');
    } catch (e) {
      setReadError(message(e, t('common.unknownError')));
    } finally { setReading(false); }
  }

  // ── 2. Signing in ─────────────────────────────────────────────────────

  async function trySignIn() {
    const endpoint = endpoints[tryAt];
    if (!endpoint) return;
    setTrying(true); setTryError(null);
    try {
      const values = Object.fromEntries(Object.entries(tryValues).filter(([, v]) => v.trim()));
      const res = await flows.testApiConnection({ ...connRef(), endpoint, values });
      setTried(res.data);
    } catch (e) {
      setTried(null);
      setTryError(message(e, t('common.unknownError')));
    } finally { setTrying(false); }
  }

  // ── 3. Choosing ───────────────────────────────────────────────────────

  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return endpoints.map((e, i) => ({ e, i })).filter(({ e }) => !q || `${e.method} ${e.path} ${e.summary ?? ''} ${e.group ?? ''}`.toLowerCase().includes(q));
  }, [endpoints, filter]);
  const toggle = (set: Set<number>, i: number, on: boolean) => {
    const next = new Set(set);
    if (on) next.add(i); else next.delete(i);
    return next;
  };

  async function draft() {
    setDraftError(null);
    const chosenActs = [...acts].sort((a, b) => a - b);
    setActions(chosenActs.map((i) => ({ add: true, index: i, name: endpointTitle(endpoints[i]) })));
    const chosenReads = [...reads].sort((a, b) => a - b);
    if (chosenReads.length === 0) { setSources([]); setStep('review'); return; }
    setDrafting(true);
    try {
      const res = await flows.draftApiSources({ ...connRef(), api_name: conn.name.trim() || doc?.title || undefined, endpoints: chosenReads.map((i) => endpoints[i]) });
      setAiNote(res.data.ai_note ?? null);
      setSources(res.data.drafts.map((d) => ({ add: true, source: d.source, examples: (d.source.examples ?? []).join('\n'), points: pointRows(d), draft: d })));
      setStep('review');
    } catch (e) {
      setDraftError(message(e, t('common.unknownError')));
    } finally { setDrafting(false); }
  }

  // ── 4. Review and add ─────────────────────────────────────────────────

  const setSource = (i: number, change: Partial<SourceRow>) => setSources((rows) => rows.map((r, j) => (j === i ? { ...r, ...change } : r)));
  const setSourceInput = (i: number, change: Partial<flows.LiveSourceInput>) => setSources((rows) => rows.map((r, j) => (j === i ? { ...r, source: { ...r.source, ...change } } : r)));
  const setPoint = (i: number, p: number, change: Partial<PointRow>) =>
    setSources((rows) => rows.map((r, j) => (j === i ? { ...r, points: r.points.map((x, k) => (k === p ? { ...x, ...change } : x)) } : r)));

  const adding = sources.filter((s) => s.add).length;
  const addingActions = actions.filter((a) => a.add).length;

  async function save() {
    setSaving(true); setSaveError(null);
    try {
      const res = await flows.setUpApiConnection({
        ...connRef(),
        sources: sources.filter((s) => s.add).map((s) => ({
          ...s.source,
          examples: s.examples.split('\n').map((x) => x.trim()).filter(Boolean),
          response_paths: pointsInput(s.points),
          interval_minutes: s.source.mode === 'refresh' ? s.source.interval_minutes ?? 60 : null
        })),
        actions: actions.filter((a) => a.add).map((a) => actionFrom(endpoints[a.index], a.name.trim() || undefined))
      });
      // The try that worked in the wizard is made once more on the saved connection, so the list shows it working.
      const tryEndpoint = endpoints[tryAt];
      if (tried?.ok && tryEndpoint) {
        const values = Object.fromEntries(Object.entries(tryValues).filter(([, v]) => v.trim()));
        await flows.testApiConnection({ connection_id: res.data.connection.id, endpoint: tryEndpoint, values }).catch(() => undefined);
      }
      notifySuccess(t('knowledge.api.toast.added', { sources: res.data.sources.length, actions: addingActions }), res.data.connection.name);
      onDone();
      onClose();
    } catch (e) {
      setSaveError(message(e, t('common.unknownError')));
    } finally { setSaving(false); }
  }

  const tryEndpoint = endpoints[tryAt];
  const readOptions = endpoints.map((e, i) => ({ e, i })).filter(({ e }) => e.kind === 'read');

  return (
    <Drawer opened={opened} onClose={onClose} position="right" size="xl"
      title={connection ? t('knowledge.api.titleMore', { name: connection.name }) : t('knowledge.api.title')}>
      <Stack gap="md" style={{ padding: 4 }} data-testid="api-wizard">
        <Stepper active={STEPS.indexOf(step)} size="xs">
          {STEPS.map((s) => <Stepper.Step key={s} label={t(`knowledge.api.step.${s}`)} />)}
        </Stepper>

        {step === 'docs' && (
          <Stack gap="sm" data-testid="api-step-docs">
            <Text role="bodySmall">{t('knowledge.api.docs.intro')}</Text>
            <Dropzone onDrop={(files) => files[0] && void readDocs({ file: files[0] })} accept={DOC_ACCEPT} maxSize={20 * 1024 * 1024} maxFiles={1} multiple={false} loading={reading}>
              <Stack gap={4} align="center" style={{ padding: 12 }}>
                <Text role="bodySmall" fw={650}>{t('knowledge.api.docs.drop')}</Text>
                <Text role="caption" c={MUTED}>{t('knowledge.api.docs.kinds')}</Text>
              </Stack>
            </Dropzone>
            <Textarea label={t('knowledge.api.docs.paste')} description={t('knowledge.api.docs.pasteHint')} value={pasted} minRows={5} maxRows={12} autosize
              onChange={(e) => setPasted(e.currentTarget.value)} data-testid="api-docs-text" />
            <Text role="caption" c={MUTED}>{t('knowledge.api.docs.cost')}</Text>
            {reading && <Text role="caption" c={MUTED}>{t('knowledge.api.docs.reading')}</Text>}
            {readError && <Alert color="error" role="alert">{readError}</Alert>}
            <Group justify="flex-end" gap="xs">
              <Button variant="default" onClick={onClose}>{t('action.cancel')}</Button>
              <Button loading={reading} disabled={!pasted.trim()} onClick={() => void readDocs({ text: pasted })} data-testid="api-docs-read">{t('knowledge.api.docs.read')}</Button>
            </Group>
          </Stack>
        )}

        {step === 'connect' && doc && (
          <Stack gap="sm" data-testid="api-step-connect">
            <Alert color="info" role="status">
              {t(read?.read_by_ai ? 'knowledge.api.connect.foundAi' : 'knowledge.api.connect.found', { count: endpoints.length, kind: t(`knowledge.api.kind.${doc.kind}`) })}
              {read?.shortened ? ` ${t('knowledge.api.connect.shortened')}` : ''}
            </Alert>
            <TextInput label={t('knowledge.api.connect.name')} description={t('knowledge.api.connect.nameHint')} value={conn.name} maxLength={120}
              onChange={(e) => setConn({ ...conn, name: e.currentTarget.value })} />
            <TextInput label={t('knowledge.api.connect.baseUrl')} description={t('knowledge.api.connect.baseUrlHint')} value={conn.base_url} placeholder="https://api.example.co.za/v1"
              onChange={(e) => setConn({ ...conn, base_url: e.currentTarget.value })} data-testid="api-base-url" />
            <HttpAuthFields
              auth={conn.auth}
              onChange={(next) => setConn({ ...conn, auth: next ?? { type: 'none' } })}
              t={t}
              secretHint={t('knowledge.api.connect.secretHint')}
              saved={connection && connection.auth.type === conn.auth.type ? connection.secret_fields : []}
              savedPlaceholder={t('knowledge.live.form.secretSaved')}
            />
            {doc.auth.note && <Text role="caption" c={MUTED}>{t('knowledge.api.connect.docsSay', { note: doc.auth.note })}</Text>}

            <div className="rr-knowledge__test" data-testid="api-try">
              <Text role="inputLabel">{t('knowledge.api.connect.tryTitle')}</Text>
              <Text role="caption" c={MUTED}>{t('knowledge.api.connect.tryHint')}</Text>
              {readOptions.length === 0 ? (
                <Text role="caption" c={MUTED}>{t('knowledge.api.connect.nothingToTry')}</Text>
              ) : (
                <>
                  <Select aria-label={t('knowledge.api.connect.tryWhich')} value={String(tryAt)}
                    onChange={(e) => { setTryAt(Number(e.currentTarget.value)); setTried(null); setTryValues({}); }}
                    data={readOptions.map(({ e, i }) => ({ value: String(i), label: `${endpointTitle(e)} (${e.method} ${e.path})` }))} />
                  {tryEndpoint && tryEndpoint.parameters.filter((p) => p.required || p.in === 'path').map((p) => (
                    <TextInput key={p.name} label={p.name} description={p.description ?? undefined} placeholder={p.example ?? undefined}
                      value={tryValues[slug(p.name)] ?? ''} onChange={(e) => setTryValues({ ...tryValues, [slug(p.name)]: e.currentTarget.value })} />
                  ))}
                  <Group gap="xs">
                    <Button size="xs" variant="default" loading={trying} disabled={!conn.base_url.trim() || !conn.name.trim()} onClick={() => void trySignIn()} data-testid="api-try-run">
                      {t('knowledge.api.connect.try')}
                    </Button>
                    {tried?.ok && <Text role="caption" c="var(--rr-color-success, #16a34a)" data-testid="api-try-ok">{t('knowledge.api.connect.tryOk', { status: tried.status })}</Text>}
                  </Group>
                </>
              )}
              {tryError && <Text role="caption" c={ERROR}>{tryError}</Text>}
              {tried && !tried.ok && <Text role="caption" c={ERROR} data-testid="api-try-failed">{tried.error ?? t('common.unknownError')}</Text>}
              {tried && tried.missing.length > 0 && <Text role="caption" c={MUTED}>{t('knowledge.api.connect.tryMissing', { names: tried.missing.join(', ') })}</Text>}
              {tried?.preview && <pre className="rr-knowledge__passage">{tried.preview}</pre>}
            </div>
            <Group justify="space-between" gap="xs">
              <Button variant="default" onClick={() => setStep('docs')}>{t('knowledge.api.back')}</Button>
              <Button disabled={!conn.name.trim() || !conn.base_url.trim()} onClick={() => setStep('choose')} data-testid="api-to-choose">{t('knowledge.api.connect.next')}</Button>
            </Group>
          </Stack>
        )}

        {step === 'choose' && doc && (
          <Stack gap="sm" data-testid="api-step-choose">
            <Text role="bodySmall">{t('knowledge.api.choose.intro')}</Text>
            {placesLeft != null && (
              <Text role="caption" c={placesLeft === 0 ? ERROR : MUTED}>{t(placesLeft === 0 ? 'knowledge.api.choose.noPlaces' : 'knowledge.api.choose.places', { count: placesLeft })}</Text>
            )}
            {endpoints.length > 10 && (
              <TextInput aria-label={t('knowledge.api.choose.filter')} placeholder={t('knowledge.api.choose.filter')} value={filter} onChange={(e) => setFilter(e.currentTarget.value)} />
            )}
            <Text role="inputLabel">{t('knowledge.api.choose.reads')}</Text>
            <Text role="caption" c={MUTED}>{t('knowledge.api.choose.readsHint', { max: maxReads })}</Text>
            {shown.filter(({ e }) => e.kind === 'read').map(({ e, i }) => (
              <Checkbox key={i} data-testid="api-choose-read" checked={reads.has(i)} disabled={!reads.has(i) && reads.size >= maxReads}
                onChange={(on) => setReads(toggle(reads, i, on))}
                label={<span><strong>{endpointTitle(e)}</strong><br /><span style={{ color: MUTED, fontSize: 12 }}>{e.method} {e.path}{e.group ? ` · ${e.group}` : ''}</span></span>} />
            ))}
            {shown.some(({ e }) => e.kind === 'action') && (
              <>
                <Text role="inputLabel">{t('knowledge.api.choose.actions')}</Text>
                <Text role="caption" c={MUTED}>{t('knowledge.api.choose.actionsHint')}</Text>
                {shown.filter(({ e }) => e.kind === 'action').map(({ e, i }) => (
                  <Checkbox key={i} data-testid="api-choose-action" checked={acts.has(i)} onChange={(on) => setActs(toggle(acts, i, on))}
                    label={<span><strong>{endpointTitle(e)}</strong><br /><span style={{ color: MUTED, fontSize: 12 }}>{e.method} {e.path}{e.group ? ` · ${e.group}` : ''}</span></span>} />
                ))}
              </>
            )}
            {reads.size > 0 && <Text role="caption" c={MUTED}>{t('knowledge.api.choose.draftCost')}</Text>}
            {drafting && <Text role="caption" c={MUTED}>{t('knowledge.api.choose.drafting')}</Text>}
            {draftError && <Alert color="error" role="alert">{draftError}</Alert>}
            <Group justify="space-between" gap="xs">
              <Button variant="default" onClick={() => setStep('connect')}>{t('knowledge.api.back')}</Button>
              <Button loading={drafting} disabled={reads.size === 0 && acts.size === 0} onClick={() => void draft()} data-testid="api-draft">
                {t(reads.size > 0 ? 'knowledge.api.choose.draft' : 'knowledge.api.choose.review')}
              </Button>
            </Group>
          </Stack>
        )}

        {step === 'review' && (
          <Stack gap="sm" data-testid="api-step-review">
            {aiNote && <Alert color="warning" role="status">{aiNote}</Alert>}
            {sources.map((row, i) => (
              <Card key={i} withBorder data-testid="api-review-source">
                <Stack gap="xs">
                  <Group justify="space-between" wrap="nowrap">
                    <Checkbox checked={row.add} onChange={(on) => setSource(i, { add: on })}
                      label={<strong>{t('knowledge.api.review.addSource', { name: row.source.name })}</strong>} />
                    <Text role="caption" c={row.draft.test_ok ? 'var(--rr-color-success, #16a34a)' : row.draft.tested ? ERROR : MUTED}>
                      {row.draft.test_ok ? t('knowledge.api.review.tried') : row.draft.tested ? t('knowledge.api.review.triedFailed', { error: row.draft.test_error ?? '' }) : t('knowledge.api.review.notTried')}
                    </Text>
                  </Group>
                  {row.add && (
                    <>
                      <div className="rr-knowledge__pair">
                        <TextInput label={t('knowledge.live.form.name')} value={row.source.name} maxLength={120} onChange={(e) => setSourceInput(i, { name: e.currentTarget.value })} />
                        <TextInput label={t('knowledge.api.review.key')} description={t('knowledge.api.review.keyHint', { example: `{{${row.source.key ?? 'key'}.${row.points.find((p) => p.on)?.key ?? 'field'}}}` })}
                          value={row.source.key ?? ''} maxLength={40} onChange={(e) => setSourceInput(i, { key: e.currentTarget.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })} />
                      </div>
                      <Textarea label={t('knowledge.live.form.answers')} description={t('knowledge.live.form.answersHint')} value={row.source.answers ?? ''} minRows={2} maxLength={500}
                        onChange={(e) => setSourceInput(i, { answers: e.currentTarget.value })} />
                      <Select label={t('knowledge.live.form.mode')} value={row.source.mode}
                        description={(row.source.parameters ?? []).length > 0 ? t('knowledge.api.review.lookupOnly') : t(`knowledge.live.cost.${row.source.mode}`, { checks: Math.round(1440 / Math.max(1, row.source.interval_minutes ?? 60)) })}
                        onChange={(e) => setSourceInput(i, { mode: e.currentTarget.value as flows.LiveSourceMode, interval_minutes: e.currentTarget.value === 'refresh' ? row.source.interval_minutes ?? 60 : null })}
                        data={[
                          { value: 'lookup', label: t('knowledge.live.mode.lookup') },
                          ...((row.source.parameters ?? []).length > 0 ? [] : [{ value: 'refresh', label: t('knowledge.live.mode.refresh') }, { value: 'manual', label: t('knowledge.live.mode.manual') }])
                        ]} />
                      {row.source.mode === 'refresh' && (
                        <Select label={t('knowledge.live.form.interval')} value={String(row.source.interval_minutes ?? 60)}
                          onChange={(e) => setSourceInput(i, { interval_minutes: Number(e.currentTarget.value) })}
                          data={flows.LIVE_SOURCE_INTERVALS.map((m) => ({ value: String(m), label: t(`knowledge.live.every.${m}`) }))} />
                      )}
                      {row.source.mode === 'lookup' && (
                        <Textarea label={t('knowledge.live.form.examples')} description={t('knowledge.live.form.examplesHint')} value={row.examples} minRows={3}
                          onChange={(e) => setSource(i, { examples: e.currentTarget.value })} />
                      )}
                      {(row.source.parameters ?? []).length > 0 && (
                        <Stack gap={4}>
                          <Text role="inputLabel">{t('knowledge.live.form.details')}</Text>
                          {(row.source.parameters ?? []).map((p, k) => (
                            <TextInput key={p.name} label={p.name} value={p.description}
                              onChange={(e) => setSourceInput(i, { parameters: (row.source.parameters ?? []).map((x, m) => (m === k ? { ...x, description: e.currentTarget.value } : x)) })} />
                          ))}
                          <Text role="caption" c={MUTED}>{t('knowledge.api.review.detailsHint')}</Text>
                        </Stack>
                      )}
                      <Stack gap={4}>
                        <Text role="inputLabel">{t('knowledge.api.review.points')}</Text>
                        <Text role="caption" c={MUTED}>{t('knowledge.api.review.pointsHint')}</Text>
                        {row.points.length === 0 && <Text role="caption" c={MUTED}>{t('knowledge.api.review.noPoints')}</Text>}
                        {row.points.map((p, k) => (
                          <div key={p.path} className="rr-knowledge__point" data-testid="api-point">
                            <Checkbox checked={p.on} onChange={(on) => setPoint(i, k, { on })} aria-label={p.path}
                              label={<code style={{ fontSize: 12 }}>{p.path}</code>} />
                            {p.on && (
                              <>
                                <TextInput aria-label={t('knowledge.live.form.pathLabel')} value={p.label} onChange={(e) => setPoint(i, k, { label: e.currentTarget.value })} />
                                <TextInput aria-label={t('knowledge.api.review.pointKey')} value={p.key} maxLength={40}
                                  onChange={(e) => setPoint(i, k, { key: e.currentTarget.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })} />
                              </>
                            )}
                          </div>
                        ))}
                      </Stack>
                      {row.draft.sample && (
                        <details>
                          <summary style={{ fontSize: 13 }}>{t('knowledge.api.review.sample')}</summary>
                          <pre className="rr-knowledge__passage">{row.draft.sample}</pre>
                        </details>
                      )}
                    </>
                  )}
                </Stack>
              </Card>
            ))}
            {actions.length > 0 && (
              <Card withBorder data-testid="api-review-actions">
                <Stack gap="xs">
                  <Text role="inputLabel">{t('knowledge.api.review.actions')}</Text>
                  <Text role="caption" c={MUTED}>{t('knowledge.api.review.actionsHint')}</Text>
                  {actions.map((a, i) => (
                    <div key={a.index} className="rr-knowledge__point">
                      <Checkbox checked={a.add} onChange={(on) => setActions((rows) => rows.map((x, j) => (j === i ? { ...x, add: on } : x)))}
                        aria-label={endpointTitle(endpoints[a.index])} label={<code style={{ fontSize: 12 }}>{endpoints[a.index].method} {endpoints[a.index].path}</code>} />
                      {a.add && <TextInput aria-label={t('knowledge.live.form.name')} value={a.name} maxLength={120}
                        onChange={(e) => setActions((rows) => rows.map((x, j) => (j === i ? { ...x, name: e.currentTarget.value } : x)))} />}
                    </div>
                  ))}
                </Stack>
              </Card>
            )}
            {saveError && <Alert color="error" role="alert">{saveError}</Alert>}
            <Group justify="space-between" gap="xs">
              <Button variant="default" onClick={() => setStep('choose')}>{t('knowledge.api.back')}</Button>
              <Button loading={saving} disabled={adding === 0 && addingActions === 0} onClick={() => void save()} data-testid="api-add">
                {t('knowledge.api.review.add', { sources: adding, actions: addingActions })}
              </Button>
            </Group>
          </Stack>
        )}
      </Stack>
    </Drawer>
  );
}
